import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { REHYDRATE } from 'redux-persist'
import {
  ACTION_ADD_CONTEXT_FILES,
  ACTION_REMOVE_CONTEXT_FILES,
  ACTION_UPDATE_CONTEXT_FILE,
  FileUploadState
} from 'components/ComposerMediaContext'
import { Article, ContentFeedType, ContentItem, Video } from 'interfaces'
import { getDestinationsSuccess, getPinterestBoards } from 'services/destinations'
import { deleteBucket, setSelectedProfiles, updateBucket } from 'services/post/actions'
import { logoutAction, setComposerProfiles } from 'services/shared/actions'
import {
  ARTICLE_TYPE,
  BRAND_BLUESKY,
  BRAND_GOOGLE,
  BRAND_INSTAGRAM,
  BRAND_PINTEREST,
  BRAND_TIKTOK,
  BRAND_TWITTER,
  BRAND_YOUTUBE,
  INPUT_ACCEPT_PHOTO,
  INPUT_ACCEPT_VIDEO,
  PHOTO_TYPE,
  POST_TYPE_ARTICLE_LINK,
  POST_TYPE_PHOTO_LINK,
  POST_TYPE_VIDEO_LINK,
  STOCK_GIF_TYPE,
  STOCK_PHOTO_TYPE,
  VIDEO_TYPE
} from 'shared/constants'
import {
  Bucket,
  COMPOSER_STATUS_KEY_GENERIC,
  ComposerBasePost,
  ComposerFacebookPost,
  ComposerPinterestPost,
  ComposerScheduleState,
  GooglePostButtonType,
  LocationInfo,
  PostComment,
  PostDestinationType,
  TikTokPostProfileSettings,
  TweetType
} from 'shared/types'
import { StoreDispatch, StoreThunkDispatch } from 'store/state'
import {
  generateBulkPostCaptionFromHtml,
  getPinterestStatusFromContent,
  getTwitterStatusFromContent,
  removeTagsFromHtml
} from 'utils/composer'
import { resetComposer } from './actions'
import { assistantExtraReducers } from './assistant'
import { createPostFailure, createPostRequest, createPostSuccess } from './create/actions'
import { facebookExtraReducers, facebookReducers } from './facebook/actions'
import { getLinkDetails } from './getLinkDetails/actions'
import { mediaExtraReducers, mediaReducers } from './media/actions'
import { multipostReducers } from './multi'
import { pinterestReducers } from './pinterest'
import { savedTextsExtraReducers } from './saved'
import { AIConversation, AIConversationMessage } from './suggest/net'
import { deleteAIConversation, fetchAIConversations } from './suggest/actions'
import {
  ComposerState,
  ComposerVideoState,
  ContentCardComposeAction,
  MediaItemState,
  PostEditType,
  initialState
} from './state'
import { tiktokReducers } from './tiktok'
import { isPresetCommentDelay, randomId, toRememberedCommentDelay } from './utils'
import { youtubeReducers } from './youtube'
import { BRAND_FACEBOOK, PostDestination } from 'shared'

export * from './assistant'
export * from './create/actions'
export * from './facebook/actions'
export * from './getLinkDetails/actions'
export * from './media/actions'
export * from './pinterest'
export * from './update/actions'
export { resetComposer }

const composerSlice = createSlice({
  name: 'composer',
  initialState: initialState(),
  reducers: {
    setComposerActiveTab: (state, action: PayloadAction<PostDestinationType>) => {
      const isStatusEdited = Object.values(state.posts).some(p => p.status.touched)
      // NOTE: If status is already present in any tab, mark all tabs as "touched"
      if (isStatusEdited) {
        Object.values(state.posts).forEach(p => {
          p.status.touched = true
        })
      }
      state.activeTab = action.payload
    },
    setComposerStatus: (
      state,
      action: PayloadAction<{ network: PostDestinationType | typeof COMPOSER_STATUS_KEY_GENERIC, text: string, html: string }>
    ) => {
      const { network, text, html } = action.payload
      const isGenericBroadcast = state.useGenericStatus && network === COMPOSER_STATUS_KEY_GENERIC
      // EXPL: hoisted out of the loop. The same html was stripped once per network key, so a
      // debounced keystroke ran ~8 identical DOMPurify passes instead of one.
      const htmlWithoutTags = isGenericBroadcast ? removeTagsFromHtml(html) : html
      Object.keys(state.posts).forEach((key: string) => {
        if (key === network) {
          state.posts[key].status.text = text
          state.posts[key].status.html = html
          state.posts[key].status.touched = true
        } else if (isGenericBroadcast) {
          if ([BRAND_PINTEREST, BRAND_YOUTUBE, BRAND_TIKTOK].includes(key)) {
            (state.posts[key as keyof typeof state.posts] as ComposerPinterestPost).description = text
          }
          if (key !== BRAND_PINTEREST && key !== BRAND_YOUTUBE) {
            (state.posts as any)[key].status.text = text
            ; (state.posts as any)[key].status.html = htmlWithoutTags
          }
          // NOTE: For twitter, if an article is added, copy the generic text and append the article url, if not present
          if (key === BRAND_TWITTER && state.contentItem?.type === ARTICLE_TYPE) {
            const articleUrl = (state.contentItem as Article).sourceLink
            if (!text.includes(articleUrl)) {
              state.posts[key].status.text = `${text}\n\n${articleUrl}`
              state.posts[key].status.html = `${text}<br/><br/>${articleUrl}`
            }
          }
        }
      })

      state.useGenericStatus = network === COMPOSER_STATUS_KEY_GENERIC ? state.useGenericStatus : false
      if (isGenericBroadcast) {
        Object.values(state.multiPost.posts).forEach(post => {
          const { html, text } = generateBulkPostCaptionFromHtml(action.payload.html, post)
          post.data.status = {
            html,
            text,
            touched: false
          }

          if ([BRAND_PINTEREST, BRAND_YOUTUBE, BRAND_TIKTOK].includes(post.network)) {
            (post.data as ComposerPinterestPost).description = text
          }
        })
      }
    },
    setComposerContent: (state, action: PayloadAction<{ content: ContentItem, sourceType?: ContentFeedType, isRepost?: boolean }>) => {
      const { content, isRepost } = action.payload
      const autoFill = content.type === ARTICLE_TYPE || content.type === VIDEO_TYPE
      const autoTwitterText = getTwitterStatusFromContent(content)
      const twitterStatus = autoFill ? {
        text: autoTwitterText,
        html: autoTwitterText,
        touched: false
      } : { ...state.posts.twitter.status }
      const blueskyStatus = autoFill ? {
        text: autoTwitterText,
        html: autoTwitterText,
        touched: false
      } : { ...state.posts.bluesky.status }
      const isImage = content.type === PHOTO_TYPE || content.type === STOCK_PHOTO_TYPE || content.type === STOCK_GIF_TYPE
      const contentImageUrl = (content as any).imageUrl
      if (isImage && !Object.values(state.images).find(img => img.url && img.url === contentImageUrl)) {
        const id = randomId()
        state.images[id] = {
          id,
          url: contentImageUrl,
          order: Object.keys(state.images).length + 1
        }
      }

      // Clear image urls if adding article or video post
      if (!isImage) {
        state.images = {}
      }
      if (isRepost && isImage) {
        const imageUrls = content.imageUrls || [content.imageUrl]
        state.images = imageUrls.reduce((acc, url, index) => {
          const id = randomId()
          acc[id] = { id, url, order: index + 1 }
          return acc
        }, {} as Record<string, MediaItemState>)
      }

      state.posts.bluesky.status = blueskyStatus
      state.posts.twitter.status = twitterStatus
      state.posts.twitter.tweetType = 'new'
      if (!state.posts.pinterest.status.touched) {
        const autoPinData = getPinterestStatusFromContent(content)
        state.posts.pinterest.title = autoPinData.title || state.posts.pinterest.title
        state.posts.pinterest.description = autoPinData.description || state.posts.pinterest.description
        state.posts.pinterest.destinationUrl = autoPinData.destinationUrl
      }
      state.posts.facebook.textBackground = undefined
      if (content.type === ARTICLE_TYPE) {
        state.posts.google.update.link = (content as Article).sourceLink || (content as Article).shortLink || ''
      }
      state.video = undefined
      state.links = content.type === ARTICLE_TYPE ? state.links.map(link => ({ ...link, selected: false })) : []
      state.postAsPhoto = false
      state.contentItem = content.imageUrls && content.imageUrls.length > 0 ? undefined : content
    },
    clearComposerContent: (state) => {
      // When removing article, mark first link (if any) as selected
      if (state.contentItem?.type === ARTICLE_TYPE && state.links.length > 0) {
        if (!state.links.some(l => l.selected)) {
          state.links[0].selected = true
        }
      }
      state.postAsPhoto = false
      state.contentItem = undefined
    },
    setRecycle: (state, action: PayloadAction<boolean | Date | number>) => {
      state.recycle = action.payload
    },
    setDraft: (state, action: PayloadAction<{ id: string, updatedAt: string } | undefined>) => {
      state.draft = action.payload
      state.postEdit = undefined
    },
    removeAIChat: (state, action: PayloadAction<string>) => {
      state.aiChats = state.aiChats.filter(c => c.id !== action.payload)
    },
    appendAIChatMessage: (state, action: PayloadAction<{ chatId: string, message: AIConversationMessage }>) => {
      const chat = state.aiChats.find(c => c.id === action.payload.chatId)
      if (chat) {
        chat.messages.push(action.payload.message)
      }
    },
    upsertAIChat: (state, action: PayloadAction<AIConversation>) => {
      const idx = state.aiChats.findIndex(c => c.id === action.payload.id)
      if (idx >= 0) {
        state.aiChats[idx] = action.payload
      } else {
        state.aiChats.unshift(action.payload)
      }
    },
    mergeState: (state, action: PayloadAction<Partial<ComposerState>>) => {
      const { payload } = action
      const defaultPosts = initialState().posts

      let processedPinterestBoards = { ...payload.pinterestBoards }
      if (payload.pinterestBoards && Object.keys(payload.pinterestBoards).length > 0) {
        const ppid = Object.keys(payload.pinterestBoards)[0]
        const currentBoardsForProfile = state.pinterestBoards[ppid] || []
        const newBoardIds = new Set(payload.pinterestBoards[ppid].map((b: any) => b.id))

        const boardsToKeep = currentBoardsForProfile
          .filter(b => !newBoardIds.has(b.id))
          .map(b => ({ ...b, selected: false }))

        processedPinterestBoards[ppid] = [...payload.pinterestBoards[ppid], ...boardsToKeep]
      }

      const igLocations = {
        ...state.posts.instagram.locations,
        ...payload.posts?.instagram?.locations
      }

      const { posts: payloadPosts, images, writer: _writer, pinterestBoards: _pb, ...restPayload } = payload

      Object.assign(state, restPayload)
      state.images = images || {}
      state.pinterestBoards = processedPinterestBoards
      state.posts = {
        ...defaultPosts,
        ...payloadPosts,
        [BRAND_INSTAGRAM]: {
          ...(payloadPosts?.instagram || state.posts.instagram),
          locations: igLocations
        }
      }
      // EXPL: set even when the payload has no `aiContent` - `Object.assign` above would otherwise keep the previous pick
      state.aiContent = Boolean(payload.aiContent)
    },
    setPostEdit: (state, action: PayloadAction<{ id: string, ppid: string, bucketId?: string, type: PostEditType } | undefined>) => {
      state.postEdit = action.payload
      state.useGenericStatus = typeof action.payload === 'undefined' ? state.useGenericStatus : false
      state.selectedBucketId = undefined
    },
    setSchedule: (state, action: PayloadAction<Partial<ComposerScheduleState>>) => {
      state.schedule = { ...state.schedule, ...action.payload }
    },
    setComposerBucket: (state, action: PayloadAction<string | undefined>) => {
      state.selectedBucketId = action.payload
      state.schedule.isActive = action.payload ? false : state.schedule.isActive
    },
    setPostAsPhoto: (state, action: PayloadAction<boolean>) => {
      state.postAsPhoto = action.payload
    },
    setAltTextEnabled: (state, action: PayloadAction<boolean>) => {
      state.altTextEnabled = action.payload
    },
    setAltText: (state, action: PayloadAction<string>) => {
      state.altText = action.payload
    },
    setNetworksView: (state, action: PayloadAction<'list' | 'tabs'>) => {
      state.networksView = action.payload
    },
    toggleGenericStatus: (state, action: PayloadAction<boolean | undefined>) => {
      const next = typeof action.payload === 'undefined' ? !state.useGenericStatus : action.payload
      const genericStatus = state.posts[COMPOSER_STATUS_KEY_GENERIC].status
      state.useGenericStatus = next
      if (next) {
        Object.values(state.posts).forEach(post => {
          post.status = { ...genericStatus }
        })

        Object.values(state.multiPost.posts).forEach(post => {
          const { html, text } = generateBulkPostCaptionFromHtml(genericStatus.html, post)
          post.data.status = {
            html,
            text,
            touched: false
          }
          if ([BRAND_PINTEREST, BRAND_YOUTUBE, BRAND_TIKTOK].includes(post.network)) {
            (post.data as ComposerPinterestPost).description = text
          }
        })
      }
    },
    setPromptId: (state, action: PayloadAction<string | undefined>) => {
      state.writer.aiWriterPromptId = action.payload
    },
    clearComposerLink: (state, action: PayloadAction<string | undefined>) => {
      if (action.payload) {
        const index = state.links.findIndex(l => l.url === action.payload)
        if (index !== -1) {
          state.links.splice(index, 1)
        }
        if (state.links[0]) {
          state.links[0].selected = true
        }
      } else {
        state.links = []
      }
      state.postAsPhoto = false
    },
    setSelectedLink: (state, action: PayloadAction<string>) => {
      state.links.forEach(link => {
        link.selected = link.url === action.payload
      })
    },
    setInstagramPostLocation: (state, action: PayloadAction<{ location: LocationInfo | undefined, ppid: string }>) => {
      const { location, ppid } = action.payload
      state.posts.instagram.locations[ppid] = location
    },
    toggleRememberIGLocation: (state) => {
      state.rememberIGLocations = !state.rememberIGLocations
    },
    setComposerGoogleButtonType: (state, action: PayloadAction<GooglePostButtonType | undefined>) => {
      state.posts.google.update.buttonType = action.payload
    },
    setComposerGoogleEventOptions: (state, action: PayloadAction<{ title?: string, startDate?: string, endDate?: string }>) => {
      if (action.payload.title !== undefined) {
        state.posts.google.event.title = action.payload.title
      }
      if (action.payload.startDate !== undefined) {
        state.posts.google.event.schedule.startDate = action.payload.startDate
      }
      if (action.payload.endDate !== undefined) {
        state.posts.google.event.schedule.endDate = action.payload.endDate
      }
    },
    setComposerGoogleOfferOptions: (state, action: PayloadAction<{
      title?: string
      startDate?: string
      endDate?: string
      couponCode?: string
      redeemOnlineUrl?: string
      termsConditions?: string
    }>) => {
      const { title, startDate, endDate, couponCode, redeemOnlineUrl, termsConditions } = action.payload
      if (title !== undefined) {
        state.posts.google.offer.event.title = title
      }
      if (startDate !== undefined) {
        state.posts.google.offer.event.schedule.startDate = startDate
      }
      if (endDate !== undefined) {
        state.posts.google.offer.event.schedule.endDate = endDate
      }
      if (couponCode !== undefined) {
        state.posts.google.offer.offer.couponCode = couponCode
      }
      if (redeemOnlineUrl !== undefined) {
        state.posts.google.offer.offer.redeemOnlineUrl = redeemOnlineUrl
      }
      if (termsConditions !== undefined) {
        state.posts.google.offer.offer.termsConditions = termsConditions
      }
    },
    setComposerGoogleLink: (state, action: PayloadAction<string>) => {
      state.posts.google.update.link = action.payload
    },
    setGooglePostType: (state, action: PayloadAction<string>) => {
      state.posts.google.selectedPostType = action.payload as typeof state.posts.google.selectedPostType
    },
    setTikTokProfileSettings: (state, action: PayloadAction<TikTokPostProfileSettings>) => {
      state.posts.tiktok.settings = {
        ...state.posts.tiktok.settings,
        [action.payload.ppid]: action.payload
      }
    },
    removePostProgress: (state, action: PayloadAction<string>) => {
      delete state.progress[action.payload]
    },
    setInstagramPostType: (state, action: PayloadAction<string | undefined>) => {
      state.posts.instagram.postType = action.payload
    },
    setInstagramTags: (state, action: PayloadAction<string[]>) => {
      state.posts.instagram.tags = action.payload
    },
    setInstagramCoverUrl: (state, action: PayloadAction<string | undefined>) => {
      state.posts.instagram.coverUrl = action.payload
    },
    promptCompose: (state, action: PayloadAction<{ content: ContentItem, action: ContentCardComposeAction }>) => {
      state.prompt = action.payload
    },
    closeComposerPrompt: (state) => {
      state.prompt = {}
    },
    setComposerTweetType: (state, action: PayloadAction<TweetType>) => {
      state.posts.twitter.tweetType = action.payload
      if ((state.contentItem?.type === ARTICLE_TYPE || state.contentItem?.type === VIDEO_TYPE) && !state.posts.twitter.status.text) {
        state.posts.twitter.status.text = (state.contentItem as Article).sourceLink || (state.contentItem as Video).shortLink || ''
        state.posts.twitter.status.html = state.posts.twitter.status.text
      }
    },
    /** Sets the AI-generated disclosure. Copied onto every network that carries it only when the post is serialized. */
    setComposerAiContent: (state, action: PayloadAction<boolean>) => {
      state.aiContent = action.payload
    },
    setComposerDocumentUpload: (state, action: PayloadAction<{ url: string, name: string } | undefined>) => {
      state.documentUpload = action.payload
    },
    setComposerLinkImage: (state, action: PayloadAction<{ linkUrl: string, imageUrl: string }>) => {
      const { linkUrl, imageUrl } = action.payload
      const link = state.links.find(l => l.url === linkUrl)
      if (link) {
        link.selectedImageUrl = imageUrl
      }
    },
    setBlueSkyFacets: (state, action: PayloadAction<any>) => {
      state.posts.bluesky.facets = action.payload
    },
    addComment: (state, action: PayloadAction<{ network: PostDestinationType }>) => {
      const { network } = action.payload
      const post = state.posts[network] as ComposerFacebookPost | undefined
      if (post && Array.isArray(post.comments)) {
        post.comments.push({
          id: randomId(),
          text: '',
          html: '',
          delay: state.lastCommentDelay
        })
      }
    },
    setComment: (state, action: PayloadAction<{ network: PostDestinationType, comment: Partial<PostComment> }>) => {
      const { network, comment } = action.payload
      const post = state.posts[network] as ComposerFacebookPost | undefined
      if (post && Array.isArray(post.comments)) {
        const comments = post.comments
        const index = comments.findIndex(c => c.id === comment.id)
        if (index !== -1) {
          Object.entries(comment).forEach(([key, value]) => {
            if (typeof value !== 'undefined') {
              (comments[index] as any)[key] = value
            }
          })
          if (typeof comment.delay === 'number') {
            state.lastCommentDelay = toRememberedCommentDelay(comment.delay)
          }
        }
      }
    },
    removeComment: (state, action: PayloadAction<{ network: PostDestinationType, commentId: string }>) => {
      const { network, commentId } = action.payload
      const post = state.posts[network] as ComposerFacebookPost | undefined
      if (post && Array.isArray(post.comments)) {
        const comments = post.comments
        const index = comments.findIndex(c => c.id === commentId)
        if (index !== -1) {
          comments.splice(index, 1)
        }
      }
    },
    setQueuePostAction: (state, action: PayloadAction<'top' | 'bottom'>) => {
      state.queuePostAction = action.payload
    },
    // Imported actions
    ...facebookReducers,
    ...mediaReducers,
    ...multipostReducers,
    ...pinterestReducers,
    ...youtubeReducers,
    ...tiktokReducers
  },
  extraReducers: builder => {
    builder
      .addCase(resetComposer, (state) => {
        const initial = initialState()
        // Seed the default comment of every network with the last used delay
        Object.values(initial.posts).forEach(post => {
          const comments = (post as ComposerFacebookPost).comments
          if (Array.isArray(comments)) {
            comments.forEach(c => { c.delay = state.lastCommentDelay })
          }
        })
        return {
          ...initial,
          lastCommentDelay: state.lastCommentDelay,
          activeTab: state.activeTab,
          selectedProfiles: state.selectedProfiles,
          selectedBucketId: state.selectedBucketId,
          savedStatuses: state.savedStatuses,
          resetKey: state.resetKey + 1,
          networksView: state.networksView,
          rememberIGLocations: state.rememberIGLocations,
          pinterestBoards: state.pinterestBoards,
          bulkUploadFile: undefined,
          queuePostAction: state.queuePostAction,
          posts: {
            ...initial.posts,
            // Do not clear selected Google Button type
            [BRAND_GOOGLE]: { ...initial.posts.google, buttonType: state.posts.google.update.buttonType },
            [BRAND_INSTAGRAM]: { ...initial.posts.instagram, locations: state.rememberIGLocations ? state.posts.instagram.locations : {} },
            [BRAND_BLUESKY]: { ...initial.posts.bluesky, facets: undefined },
            [BRAND_FACEBOOK]: { ...initial.posts.facebook, lastUsedVideoPostType: state.posts.facebook.lastUsedVideoPostType }
          },
          facebookBackgroundTemplates: state.facebookBackgroundTemplates,
          defaultFacebookBackgroundTemplateId: state.defaultFacebookBackgroundTemplateId
        }
      })
      .addCase(logoutAction, () => initialState())
      .addCase(updateBucket.fulfilled, (state, action) => {
        const data = (action as any).payload
        const bucket: Bucket = data.bucket
        if (bucket.id === state.selectedBucketId) {
          state.selectedProfiles = data.profiles.map((p: PostDestination) => p.id)
        }
      })
      .addCase(deleteBucket.fulfilled, (state, action) => {
        const deletedBucketId = (action as any).payload
        state.selectedBucketId = state.selectedBucketId === deletedBucketId ? undefined : state.selectedBucketId
      })
      .addCase(setSelectedProfiles, (state, action) => {
        const ids: string[] = []
        const types: { [key: string]: boolean } = {}
        action.payload.profiles.forEach((profile: PostDestination) => {
          ids.push(profile.id)
          types[profile.type] = true
        })

        const networks = Object.keys(types)
        state.activeTab = networks.includes(state.activeTab) ? state.activeTab : networks[0] as PostDestinationType
        state.selectedProfiles = ids
      })
      .addCase(getDestinationsSuccess, (state, action) => {
        if (state.selectedProfiles.length === 0) {
          const ids: string[] = []
          action.payload.forEach((p: PostDestination) => {
            if (p.connected) {
              ids.push(p.id)
            }
          })
          state.selectedProfiles = ids
        }
      })
      .addCase(getLinkDetails.fulfilled, (state, action) => {
        const { link, isPinterestDestinationUrl } = action.payload
        // Do not set link if type is missing and link has no title or files are uploading
        const isUploading = Object.values(state.images).some(item => item.isUploading) || state.video?.isUploading
        if ((!link.type && !link.title) || isUploading) {
          return
        }

        let statusTouched = false
        if (isPinterestDestinationUrl) {
          for (const network of Object.keys(state.posts)) {
            if (network === BRAND_PINTEREST || network === COMPOSER_STATUS_KEY_GENERIC) {
              continue
            }
            if (((state.posts as any)[network] as ComposerBasePost).status.touched) {
              statusTouched = true
              break
            }
          }
        }

        const shouldUpdateGlobalStateLink = !isPinterestDestinationUrl
          || (!statusTouched && !state.video?.url && Object.keys(state.images).length === 0 && !isUploading)
        const url = link.url.split('?')[0]
        const linkExt = url.substring(url.lastIndexOf('.'))
        const isNativeVideoLink = INPUT_ACCEPT_VIDEO.includes(linkExt?.toLowerCase() + '')
        const isImageLink = INPUT_ACCEPT_PHOTO.includes(linkExt?.toLowerCase())

        if (isImageLink) {
          const id = randomId()
          state.images[id] = { id, url: link.image[0], order: Object.keys(state.images).length + 1 }
          state.links = []
          if (!isPinterestDestinationUrl) {
            state.video = undefined
          }
          state.posts.facebook.textBackground = undefined
          return
        }

        if (isNativeVideoLink) {
          const id = randomId()
          state.video = isPinterestDestinationUrl ? { id, url: link.url } : undefined
          state.images = {}
          state.postAsPhoto = false
          state.links = []
          state.posts.facebook.textBackground = undefined
          return
        }

        state.posts.pinterest.destinationUrl = state.posts.pinterest.destinationUrl || link.url
        state.posts.pinterest.title = state.posts.pinterest.title || link.title || ''
        state.posts.pinterest.description = state.posts.pinterest.description || link.description || ''
        state.posts.google.update.link = link.url

        const newLink = Object.assign({}, link)
        newLink.images = [
          ...link.image,
          ...(link.images || [])
        ]
        newLink.selectedImageUrl = newLink.images[0]
        newLink.selected = state.links.length === 0 && state.contentItem?.type !== ARTICLE_TYPE
        // If link type has invalid value, set it to the default "link"
        if (![POST_TYPE_PHOTO_LINK, POST_TYPE_VIDEO_LINK, POST_TYPE_ARTICLE_LINK].includes(newLink.type)) {
          newLink.type = POST_TYPE_ARTICLE_LINK
        }

        if (shouldUpdateGlobalStateLink) {
          if (!state.links.find(l => l.url === newLink.url)) {
            state.links.push(newLink)
          }
        }
        if (!isPinterestDestinationUrl) {
          state.video = undefined
        }
        state.postAsPhoto = false
        state.posts.facebook.textBackground = undefined
      })
      .addCase(createPostSuccess, (state, action) => {
        state.progress[action.payload.id] = { post: action.payload.post, error: undefined, complete: true }
      })
      .addCase(createPostRequest, (state, action) => {
        const { post, id } = action.payload
        state.progress[id] = { post, error: undefined, complete: false }
      })
      .addCase(createPostFailure, (state, action) => {
        const { error, source } = action.payload
        const { post, id } = source
        state.progress[id] = { post, error: error.message, complete: true }
      })
      .addCase(getPinterestBoards.fulfilled, (state, action) => {
        const { ppid, boards } = action.payload
        const boardsArray = Object.values(boards)
        const current = state.pinterestBoards[ppid] || []
        const selectedBoardId = current.find(b => b.selected)?.id
        state.pinterestBoards[ppid] = boardsArray.map((b: any) => ({ ...b, selected: b.id === selectedBoardId }))
      })
      .addCase(setComposerProfiles, (state, action: PayloadAction<PostDestination[]>) => {
        const ids: string[] = []
        const types: { [key: string]: boolean } = {}
        action.payload.forEach(profile => {
          ids.push(profile.id)
          types[profile.type] = true
        })

        const networks = Object.keys(types)
        state.activeTab = networks.includes(state.activeTab) ? state.activeTab : networks[0] as PostDestinationType
        state.selectedProfiles = ids
      })
      .addCase(fetchAIConversations.fulfilled, (state, action) => {
        state.aiChats = action.payload
      })
      .addCase(deleteAIConversation.fulfilled, (state, action) => {
        state.aiChats = state.aiChats.filter(c => c.id !== action.payload)
      })

    // Imported extra reducers
    facebookExtraReducers(builder)
    savedTextsExtraReducers(builder)
    assistantExtraReducers(builder)
    mediaExtraReducers(builder)

    // redux-persist restores lastCommentDelay but NOT posts (not whitelisted), so the
    // freshly-seeded default comments still carry delay 0. Re-apply the remembered delay
    // to them on rehydrate. Matcher must stay last — addCase cannot follow addMatcher.
    builder.addMatcher(
      (action: any) => action.type === REHYDRATE && action.key === 'composer',
      (state, action: any) => {
        const delay = action.payload?.lastCommentDelay
        if (typeof delay === 'number' && isPresetCommentDelay(delay)) {
          state.lastCommentDelay = delay
          Object.values(state.posts).forEach(post => {
            const comments = (post as ComposerFacebookPost).comments
            if (Array.isArray(comments)) {
              comments.forEach(c => { c.delay = delay })
            }
          })
        }
      }
    )
  },
  selectors: {
    getSelectedProfiles: state => state.selectedProfiles,
    getQueuePostAction: state => state.queuePostAction
  }
})

export const {
  setComposerActiveTab,
  setComposerStatus,
  setComposerContent,
  clearComposerContent,
  setRecycle,
  setDraft,
  mergeState,
  setPostEdit,
  setSchedule,
  setComposerBucket,
  setPostAsPhoto,
  setAltTextEnabled,
  setAltText,
  setNetworksView,
  toggleGenericStatus,
  setPromptId,
  clearComposerLink,
  setSelectedLink,
  setComposerGoogleButtonType,
  setComposerGoogleLink,
  setInstagramPostLocation,
  toggleRememberIGLocation,
  setTikTokProfileSettings,
  removePostProgress,
  selectComposerFBAlbum,
  toggleFBPostReshare,
  setFBPostType,
  setFacebookTextBackground,
  setComposerBulkUploadFileUrl,
  setInstagramPostType,
  setInstagramTags,
  setInstagramCoverUrl,
  promptCompose,
  closeComposerPrompt,
  addArticlePosts,
  addImagePosts,
  addVideoPosts,
  addImageUploadPosts,
  addMultipostVideoFiles,
  applyGenericStatusToPosts,
  editMultiPosts,
  handleMultiPostError,
  imageUploadUpdate,
  setMultiPostTweetTypeAll,
  imagesUploadDone,
  initializeTikTokSettings,
  removeMultiPosts,
  setMultiPostNetworkSettings,
  setMultiPostError,
  setMultiPostIGLocations,
  reorderMultiPosts,
  resetMultiPostState,
  setMultiPostFBAlbums,
  setMultiPostFBReshareAll,
  setMultiPostPiBoards,
  setMultiPostsRecycle,
  setMultipostTikTokProfileSettings,
  setMultiPostAiContent,
  setMultipostComment,
  setMultipostCommentDelay,
  removeMultipostComment,
  addMultiPostComment,
  updateArticlePost,
  clearPinterestBoards,
  selectPinterestBoard,
  setPinDescription,
  setPinDestinationUrl,
  setPinTitle,
  setPinterestProfileBoards,
  setYtDescription,
  setComposerTweetType,
  setYtPrivacy,
  setYtTitle,
  setComposerDocumentUpload,
  setQueuePostAction,
  setComposerLinkImage,
  setBlueSkyFacets,
  setMultipostBlueskyFacets,
  setTiktokPhotoDescription,
  setTiktokPhotoTitle,
  videoFileAdded,
  setComposerGoogleEventOptions,
  setComposerGoogleOfferOptions,
  setGooglePostType,
  setComment,
  addComment,
  removeComment,
  setDefaultFBBackgroundTemplateId,
  removeAIChat,
  appendAIChatMessage,
  upsertAIChat,
  setComposerAiContent,
  setPinAiContentMode
} = composerSlice.actions

const { addImages, addVideo, removeImages, removeVideo, reorderImages, updateImage, updateVideo } = composerSlice.actions

export default composerSlice.reducer
export const { getSelectedProfiles, getQueuePostAction } = composerSlice.selectors

export const addComposerImages = ({ items, contextDispatch }: {
  items: Array<Omit<MediaItemState & Partial<FileUploadState>, 'id'>>,
  contextDispatch?: React.Dispatch<any>
}) => {
  return (dispatch: StoreDispatch) => {
    const itemsWithIds = items.map(item => ({ id: randomId(), ...item }))
    const storeImages = itemsWithIds.map(item => {
      const storeItem = { ...item }
      delete (storeItem as any).file
      delete (storeItem as any).upload
      return storeItem
    })
    dispatch(addImages(storeImages))

    // NOTE: Add uploads to media context
    const fileUploads = itemsWithIds.filter(i => i.file)
    if (contextDispatch && fileUploads.length > 0) {
      contextDispatch({ type: ACTION_ADD_CONTEXT_FILES, payload: fileUploads })
    }
  }
}

export const removeComposerImages = ({ ids, contextDispatch }: {
  ids: string[],
  contextDispatch: React.Dispatch<any>
}) => {
  return (dispatch: StoreDispatch) => {
    dispatch(removeImages(ids))
    // NOTE: Remove uploads from media context
    contextDispatch({ type: ACTION_REMOVE_CONTEXT_FILES, payload: ids })
  }
}

/**
 * Renumber every image in one action — `ids` is the new order, first is the featured one.
 * Re-exported as-is rather than wrapped: an arrow function would drop `.type`, `.match` and
 * `toString()`, which builder.addCase, isAnyOf and the devtools all read.
 */
export const reorderComposerImages = reorderImages

export const updateComposerImage = ({ item, contextDispatch }: {
  item: { id: string } & Partial<MediaItemState & Partial<FileUploadState>>,
  contextDispatch: React.Dispatch<any>
}) => {
  return (dispatch: StoreDispatch) => {
    const storeItem = { ...item }
    delete (storeItem as any).file
    delete (storeItem as any).upload
    dispatch(updateImage(storeItem))
    if (item.upload || item.file) {
      contextDispatch({ type: ACTION_UPDATE_CONTEXT_FILE, payload: item })
    }
  }
}

export const addComposerVideo = ({ video, contextDispatch }: {
  video: { url?: string, file?: File, isEdited?: boolean, name?: string },
  contextDispatch?: React.Dispatch<any>
}) => {
  return (dispatch: StoreDispatch) => {
    const id = randomId()
    dispatch(addVideo({ id, url: video.url, name: video.name ?? video.file?.name, isEdited: video.isEdited }))
    // Only a real local File belongs in the media context — URL-sourced videos have no upload.
    if (contextDispatch && video.file instanceof File) {
      contextDispatch({
        type: ACTION_ADD_CONTEXT_FILES,
        payload: [{ ...video, isVideo: true, id }]
      })
    }
  }
}

export const removeComposerVideo = ({ id, contextDispatch }: { id: string, contextDispatch: React.Dispatch<any> }) => {
  return (dispatch: StoreDispatch) => {
    dispatch(removeVideo())
    // NOTE: Remove upload from media context
    contextDispatch({ type: ACTION_REMOVE_CONTEXT_FILES, payload: [id] })
  }
}

export const updateComposerVideo = ({ item, contextDispatch }: {
  item: { id: string } & Partial<ComposerVideoState & Partial<FileUploadState>>,
  contextDispatch: React.Dispatch<any>
}) => {
  return (dispatch: StoreDispatch) => {
    const { file, upload, ...rest } = item
    dispatch(updateVideo(rest))
    if (item.upload || item.file) {
      contextDispatch({ type: ACTION_UPDATE_CONTEXT_FILE, payload: { id: item.id, file, upload } })
    }
  }
}

export function setComposerFile({ url, name }: { url: string, name?: string }) {
  return (dispatch: StoreThunkDispatch) => {
    const linkExt = url.split('?')[0].substring(url.lastIndexOf('.'))
    const isImageLink = INPUT_ACCEPT_PHOTO.includes(linkExt?.toLowerCase())
    if (isImageLink) {
      return dispatch(addComposerImages({ items: [{ url }] }))
    }

    const isNativeVideoLink = INPUT_ACCEPT_VIDEO.includes(linkExt?.toLowerCase() + '')
    if (isNativeVideoLink) {
      return dispatch(addComposerVideo({ video: { url, file: { name } as any } }))
    }
    return null
  }
}
